// SPEC-455 M1 — shared text physics: measurement, wrapping, derived sizing.
// Pure TS, zero runtime dependencies — same constraints as layout.ts (must not import
// react/konva/store or any web-internal module). Both the web bundle and the CF worker
// consume this module at build time (agent.ts/tools.ts import it relative, esbuild/tsc
// inline it), so worker-side brief projection and front-end rendering derive identical
// w/h from the same code path (SPEC-455 acceptance: 双端一致).
//
// Measurement model:
// - Latin/digits/punctuation: real Space Grotesk horizontal advances (hmtx, regular +
//   bold) extracted by tools/gen-font-metrics.mjs into fontMetrics.generated.ts — the
//   canvas font stack is "Space Grotesk", "Noto Sans SC", … (Artboard CANVAS_FONT).
// - CJK (rendered by Noto Sans SC): full-width 1.0em approximation (allowed by the
//   SPEC-455 M1 brief); halfwidth katakana 0.5em.
// - Unknown codepoints: deterministic heuristic (≥0x2000 → 1.0em, else 0.6em).
// - Kerning is ignored: advance sums are ≥ browser measureText widths for typical
//   (negative-kern) pairs, and ceilPx rounds up — derived boxes never come out narrower
//   than the browser's measured text. The tolerance is pinned against a real browser
//   canvas-measureText fixture in worker/test/spec455.test.mjs.
// Rounding: single source of truth — every derived pixel size goes through ceilPx.

import { SPACE_GROTESK_REGULAR, SPACE_GROTESK_BOLD, SPACE_GROTESK_UNITS_PER_EM } from './fontMetrics.generated';

/** House line-box convention (matches SYSTEM_PROMPT「文字高 ≈ font_size×1.4」and the legacy add_text height estimate). */
export const LINE_HEIGHT_RATIO = 1.4;

/** The only rounding rule for derived pixel sizes: round up, so a derived box never clips its content. */
export const ceilPx = (v: number): number => Math.ceil(v);

/** sizing field values (SPEC-455 M1). text: auto/fixed-width; frame: fit_content/fill_container. */
export type TextSizing = 'auto' | 'fixed-width';
export type FrameSizing = 'fit_content' | 'fill_container';
export type SizingValue = TextSizing | FrameSizing;
export const TEXT_SIZING_VALUES: readonly TextSizing[] = ['auto', 'fixed-width'];
export const FRAME_SIZING_VALUES: readonly FrameSizing[] = ['fit_content', 'fill_container'];

/** Injectable single-line width measurer (px). Default = the shared metrics table. */
export type TextMeasurer = (text: string, fontSize: number, fontWeight?: string) => number;

/** Fullwidth script ranges (Noto Sans SC renders these at 1.0em; halfwidth katakana at 0.5em). */
const CJK_1EM_RANGES: readonly [number, number][] = [
  [0x1100, 0x115f], // Hangul Jamo
  [0x2e80, 0x9fff], // CJK radicals … unified ideographs (incl. kana, bopomofo, CJK punctuation)
  [0xac00, 0xd7af], // Hangul syllables
  [0xf900, 0xfaff], // CJK compatibility ideographs
  [0xfe30, 0xfe4f], // CJK compatibility forms
  [0xff00, 0xff60], // fullwidth forms
  [0xffe0, 0xffe6], // fullwidth signs
  [0x20000, 0x2fa1f], // CJK ext B+ and compatibility supplement
];

export function isCjkCodePoint(cp: number): boolean {
  return CJK_1EM_RANGES.some(([lo, hi]) => cp >= lo && cp <= hi);
}

/** Advance width of one codepoint, in em. Table → CJK rule → deterministic fallback. */
export function charAdvanceEm(cp: number, bold: boolean): number {
  const table = bold ? SPACE_GROTESK_BOLD : SPACE_GROTESK_REGULAR;
  const units = table[cp];
  if (units !== undefined) return units / SPACE_GROTESK_UNITS_PER_EM;
  if (isCjkCodePoint(cp)) return cp >= 0xff61 && cp <= 0xffdc ? 0.5 : 1;
  return cp >= 0x2000 ? 1 : 0.6;
}

/** Single-line text width in px (no wrapping; explicit '\n' are summed per-line by callers). */
export const measureTextWidthPx: TextMeasurer = (text, fontSize, fontWeight) => {
  const bold = fontWeight === 'bold';
  let em = 0;
  for (const ch of String(text ?? '')) em += charAdvanceEm(ch.codePointAt(0) as number, bold);
  return em * fontSize;
};

/**
 * Wrapped line list for a fixed-width text box. Mirrors Konva.Text (konva 9.3.16)
 * `_setTextData` with wrap='word' exactly — split on '\n', then per paragraph: binary
 * search the longest measured prefix that fits maxWidth; if the char after the prefix is
 * a space/dash keep the break there, else pull back to the last space/dash inside the
 * prefix; with neither (CJK runs) break at the fitted char boundary. A paragraph whose
 * first char alone exceeds maxWidth stops wrapping (same as Konva).
 */
export function wrapTextLines(
  text: string, maxWidth: number, fontSize: number, fontWeight?: string,
  measure: TextMeasurer = measureTextWidthPx,
): string[] {
  const out: string[] = [];
  for (const para of String(text ?? '').split('\n')) {
    if (!(maxWidth > 0) || measure(para, fontSize, fontWeight) <= maxWidth) { out.push(para); continue; }
    let line = para;
    while (line.length > 0) {
      const chars = Array.from(line);
      let lo = 0, hi = chars.length, fitLen = 0;
      while (lo < hi) {
        const mid = (lo + hi + 1) >>> 1;
        if (measure(chars.slice(0, mid).join(''), fontSize, fontWeight) <= maxWidth) { fitLen = mid; lo = mid; }
        else hi = mid - 1;
      }
      if (fitLen === 0) break; // single char wider than the box → Konva stops here
      const fitStr = chars.slice(0, fitLen).join('');
      const nextChar = chars[fitLen];
      let low = fitLen;
      if (nextChar !== ' ' && nextChar !== '-') {
        const wrapIndex = Math.max(fitStr.lastIndexOf(' '), fitStr.lastIndexOf('-')) + 1;
        if (wrapIndex > 0) low = wrapIndex;
      }
      out.push(chars.slice(0, low).join('').trimEnd());
      line = chars.slice(low).join('').trimStart();
      if (line.length > 0 && measure(line, fontSize, fontWeight) <= maxWidth) { out.push(line); break; }
    }
  }
  return out;
}

/** Derived size of a text object under engine sizing (SPEC-455 M1). */
export interface DerivedTextSize { w: number; h: number }

/**
 * 'auto' = shrink-wrap: w = widest explicit line, h = lines × font_size × ratio (no wrapping).
 * 'fixed-width' = w honored as given, h derived from the wrapped line count at that width.
 * Defensive: 'fixed-width' without a usable w (>0 finite) falls back to 'auto' derivation.
 */
export function deriveTextSize(
  text: string, fontSize: number, fontWeight: string | undefined,
  sizing: TextSizing, w: number | undefined,
  measure: TextMeasurer = measureTextWidthPx,
): DerivedTextSize {
  const src = String(text ?? '');
  const fs = Number.isFinite(fontSize) && fontSize > 0 ? fontSize : 48;
  if (sizing === 'fixed-width' && typeof w === 'number' && Number.isFinite(w) && w > 0) {
    const lines = wrapTextLines(src, w, fs, fontWeight, measure);
    return { w, h: ceilPx(lines.length * fs * LINE_HEIGHT_RATIO) };
  }
  const paras = src.split('\n');
  let maxW = 0;
  for (const p of paras) maxW = Math.max(maxW, measure(p, fs, fontWeight));
  return { w: ceilPx(maxW), h: ceilPx(paras.length * fs * LINE_HEIGHT_RATIO) };
}
